import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { confirmSignUp, resendConfirmationCode } from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, MailCheck, RefreshCw } from "lucide-react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { YesChefLogo } from "@/components/common/YesChefLogo";

const confirmSchema = z.object({
  code: z
    .string()
    .length(6, { error: () => t`Code must be exactly 6 digits` })
    .regex(/^\d+$/, { error: () => t`Code must contain only digits` }),
});

type ConfirmForm = z.infer<typeof confirmSchema>;

export const Route = createFileRoute("/(public)/confirm-email")({
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : "",
    code: typeof search.code === "string" ? search.code : "",
  }),
  component: ConfirmEmailComponent,
});

function ConfirmEmailComponent() {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { email, code: codeParam } = Route.useSearch();
  const autoConfirmAttempted = useRef(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate({ to: "/dashboard" });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const [confirmed, setConfirmed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const form = useForm<ConfirmForm>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { code: codeParam || "" },
  });

  const confirmMutation = useMutation({
    mutationFn: confirmSignUp,
    onSuccess: () => {
      setConfirmed(true);
    },
    onError: (error: Error) => {
      form.setValue("code", "");
      toast.error(error.message || i18n._("Verification failed. Please try again."));
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => resendConfirmationCode(email),
    onSuccess: () => {
      toast.success(i18n._("New code sent! Check your inbox."));
      setResendCooldown(60);
    },
    onError: (error: Error) => {
      toast.error(error.message || i18n._("Could not resend code."));
    },
  });

  // Auto-confirm when code arrives via URL (future email link support)
  useEffect(() => {
    if (codeParam && email && !autoConfirmAttempted.current) {
      autoConfirmAttempted.current = true;
      confirmMutation.mutate({ email, code: codeParam });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const onSubmit = (data: ConfirmForm) => {
    confirmMutation.mutate({ email, code: data.code });
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, "$1***$3")
    : i18n._("your email");

  const isPending = confirmMutation.isPending;

  return (
    <div className="flex min-h-screen bg-(--brand-white)">
      {/* Left — kitchen photo */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=800&h=1200&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-(--color-scrim-photo-wash)" />
        <div className="absolute inset-0 bg-linear-to-r from-transparent to-white" />
      </div>

      {/* Right — content panel */}
      <div
        className="flex-1 flex flex-col items-center justify-between py-20 px-8 lg:px-30 bg-(--brand-white)"
      >
        <div className="self-start">
          <YesChefLogo />
        </div>

        <div className="w-full max-w-122 flex flex-col gap-6">
          {confirmed ? (
            <SuccessView onContinue={() => navigate({ to: "/login" })} />
          ) : (
            <>
              {/* Heading */}
              <div className="flex flex-col gap-2 text-center">
                <div className="flex justify-center mb-2">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center bg-(--gray-100)"
                  >
                    <MailCheck className="w-7 h-7 text-(--brand-black)" />
                  </div>
                </div>
                <h1
                  className="font-bold leading-tight text-(--brand-black)"
                  style={{ fontSize: 40 }}
                >
                  {i18n._("Verify your email")}
                </h1>
                <p className="text-base text-(--gray-500)">
                  {i18n._("We sent a 6-digit code to")}{" "}
                  <span className="font-semibold text-(--brand-black)">
                    {maskedEmail}
                  </span>
                  {". "}
                  {i18n._("Enter it below to activate your account.")}
                </p>
              </div>

              {/* Auto-verifying state */}
              {isPending && codeParam ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-(--brand-black)" />
                  <p className="text-sm text-(--gray-500)">
                    {i18n._("Verifying your email...")}
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-4"
                >
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="code"
                      className="text-sm font-medium text-(--brand-black)"
                    >
                      {i18n._("Verification code")}
                    </Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      autoComplete="one-time-code"
                      className="figma-input text-center text-2xl tracking-[0.5em]"
                      {...form.register("code")}
                      disabled={isPending}
                    />
                    {form.formState.errors.code && (
                      <p className="text-xs text-(--destructive)">
                        {form.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isPending}
                    className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-[6px] transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 bg-(--brand-black)"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {i18n._("Verifying...")}
                      </>
                    ) : (
                      <>
                        {i18n._("Verify email")}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Resend */}
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm text-(--gray-500)">
                  {i18n._("Didn't receive it?")}
                </p>
                <button
                  type="button"
                  disabled={resendCooldown > 0 || resendMutation.isPending}
                  onClick={() => resendMutation.mutate()}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-70 text-(--brand-black)"
                >
                  {resendMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {resendCooldown > 0
                    ? t`Resend in ${resendCooldown}s`
                    : i18n._("Resend code")}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-sm text-(--gray-500)">
          {i18n._("Wrong account?")}{" "}
          <Link
            to="/sign-up"
            className="font-semibold hover:underline text-(--gray-600)"
          >
            {i18n._("Sign up again")}
          </Link>
        </p>
      </div>

    </div>
  );
}

function SuccessView({ onContinue }: { onContinue: () => void }) {
  const { i18n } = useLingui();

  useEffect(() => {
    const timer = setTimeout(onContinue, 3000);
    return () => clearTimeout(timer);
  }, [onContinue]);

  return (
    <div className="flex flex-col items-center gap-6 text-center py-8">
      <div
        className="flex h-16 w-16 items-center justify-center rounded-full bg-(--color-success)/10"
      >
        <MailCheck className="h-8 w-8 text-(--color-success-text)" />
      </div>
      <div className="flex flex-col gap-2">
        <h2
          className="font-bold text-(--brand-black)"
          style={{ fontSize: 32 }}
        >
          {i18n._("Email verified!")}
        </h2>
        <p className="text-base text-(--gray-500)">
          {i18n._("Your account is active. Redirecting you to login...")}
        </p>
      </div>
      <button
        onClick={onContinue}
        className="flex items-center gap-2 text-sm font-semibold hover:opacity-70 transition-opacity text-(--brand-black)"
      >
        {i18n._("Go to login")}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
